import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useSpring, AnimatePresence } from 'framer-motion';
import { 
  Code, Cpu, Brain, ClipboardList, 
  Monitor, Database, ChevronRight, 
  CheckCircle2, Search, PenTool, TrendingUp,
  Wrench, ExternalLink, Shield, ArrowRight,
  Terminal, Send, Wifi, AlertTriangle, RefreshCw, Power,
  MapPin, Phone, Mail, Github, Linkedin, Copy, Check,
  BarChart3, PieChart, Activity, Server, Lock
} from 'lucide-react';
import { useLanguage } from './LanguageContext';
import { Reveal, RevealGroup, RevealItem } from './Reveal';

// --- SHARED COMPONENTS (Exported for individual views) ---

const TypingText: React.FC<{ text: string; delay?: number; className?: string }> = ({ text, delay = 50, className }) => {
  const [displayedText, setDisplayedText] = useState('');
  
  useEffect(() => {
    setDisplayedText('');
    let i = 0;
    const interval = setInterval(() => {
      setDisplayedText(text.slice(0, i + 1));
      i++;
      if (i > text.length) clearInterval(interval);
    }, delay);
    return () => clearInterval(interval);
  }, [text, delay]);

  return <span className={className}>{displayedText}<span className="animate-cursor-blink text-emerald-600 dark:text-terminal-green">|</span></span>;
};

export const HeroSection: React.FC<{ onNavigate: (view: string) => void }> = ({ onNavigate }) => {
  const { t } = useLanguage();
  return (
    <section className="min-h-screen pt-20 flex flex-col items-center justify-center relative overflow-hidden px-4 transition-colors duration-300">
      {/* Background Matrix Rain Effect (Subtle) */}
      <div className="absolute inset-0 pointer-events-none opacity-0 dark:opacity-5 flex justify-between px-10 text-[10px] text-terminal-green select-none overflow-hidden transition-opacity duration-300">
         {Array.from({ length: 6 }).map((_, i) => (
           <div key={i} className="animate-matrix-flow space-y-4">
             {Array.from({ length: 20 }).map((_, j) => (
               <div key={j}>{Math.random() > 0.5 ? '1' : '0'}</div>
             ))}
           </div>
         ))}
      </div>
      
      <div className="max-w-6xl w-full text-center z-10 space-y-8 flex flex-col items-center">
        <Reveal width="100%">
          <h1 className="text-5xl md:text-8xl lg:text-9xl font-black tracking-tighter text-slate-900 dark:text-white mb-6 glitch-hover cursor-default transition-colors leading-[0.9]">
            GRIND UNTIL<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-600 to-cyan-600 dark:from-terminal-green dark:to-terminal-cyan">
              IT WORKS.
            </span>
          </h1>
        </Reveal>

        <div className="h-16 md:h-8 flex items-center justify-center">
          <Reveal delay={0.2}>
            <TypingText 
              text="AI-Driven Solutions & Full-Stack Excellence"
              className="text-slate-600 dark:text-gray-400 text-lg md:text-2xl font-mono tracking-wide"
              delay={30}
            />
          </Reveal>
        </div>

        <Reveal delay={0.4}>
          <div className="flex flex-col sm:flex-row gap-6 mt-8">
            <button 
              onClick={() => onNavigate('projects')}
              className="px-8 py-4 bg-slate-900 dark:bg-white text-white dark:text-black font-bold text-lg hover:bg-emerald-600 dark:hover:bg-terminal-green transition-all duration-300 shadow-lg"
            >
              [ View Projects ]
            </button>
            <button 
              onClick={() => onNavigate('contact')}
              className="px-8 py-4 bg-transparent border-2 border-slate-900 dark:border-white text-slate-900 dark:text-white font-bold text-lg hover:bg-slate-100 dark:hover:bg-white/10 transition-all duration-300"
            >
              Get a Quote <ArrowRight size={20} className="inline ml-2" />
            </button>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const ServicesSection: React.FC = () => {
  const { t } = useLanguage();
  const services = [
    { icon: <Code size={32} />, title: "Full-Stack Web", desc: "React, PHP, SQL. From frontend beauty to backend logic. Systems that scale.", color: "text-emerald-600 dark:text-terminal-green" },
    { icon: <Cpu size={32} />, title: "IoT & Hardware", desc: "Connecting the physical world. ESP32, Sensors, and Real-time Data processing.", color: "text-cyan-600 dark:text-terminal-cyan" },
    { icon: <Brain size={32} />, title: "AI Integration", desc: "Empowering legacy systems with modern AI capabilities and LLM integration.", color: "text-purple-600 dark:text-purple-400" },
    { icon: <ClipboardList size={32} />, title: "IT Consulting", desc: "Turning chaos into structured deliverables. Agile workflows for hard deadlines.", color: "text-amber-500 dark:text-yellow-400" }
  ];

  return (
    <section id="services" className="py-24 bg-slate-50/80 dark:bg-[#080808]/80 transition-colors duration-300 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-16">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white flex items-center gap-3 mb-4">
              <span className="text-emerald-600 dark:text-terminal-green">./</span>{t.services.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="h-1 w-20 bg-emerald-600 dark:bg-terminal-green"></div>
          </Reveal>
        </div>

        <RevealGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {services.map((service, idx) => (
            <RevealItem key={idx} className="h-full">
              <motion.div 
                whileHover={{ y: -5 }}
                className="bg-white/50 dark:bg-[#0f0f0f]/50 border border-slate-200 dark:border-white/10 p-8 relative group overflow-hidden shadow-sm hover:shadow-xl dark:hover:shadow-none transition-all duration-300 h-full backdrop-blur-md"
              >
                <div className={`absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-100 transition-opacity ${service.color}`}>
                  <Monitor size={48} strokeWidth={1} />
                </div>
                <div className={`mb-6 ${service.color}`}>{service.icon}</div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 group-hover:text-emerald-600 dark:group-hover:text-terminal-green transition-colors">{service.title}</h3>
                <p className="text-slate-600 dark:text-gray-400 text-sm leading-relaxed">{service.desc}</p>
              </motion.div>
            </RevealItem>
          ))}
        </RevealGroup>
      </div>
    </section>
  );
};

// --- MOCKUP COMPONENTS FOR PROJECT PREVIEWS ---

const BrowserFrame: React.FC<{ children: React.ReactNode; url: string }> = ({ children, url }) => (
  <div className="rounded-lg overflow-hidden border border-slate-200 dark:border-white/10 bg-white dark:bg-[#0a0a0a] shadow-2xl">
    {/* Browser Header */}
    <div className="bg-slate-100 dark:bg-[#151515] px-4 py-2 border-b border-slate-200 dark:border-white/10 flex items-center gap-4">
      <div className="flex gap-1.5">
        <div className="w-2.5 h-2.5 rounded-full bg-red-400/80"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-amber-400/80"></div>
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-400/80"></div>
      </div>
      <div className="flex-1 bg-white dark:bg-black/50 rounded px-3 py-1 text-[10px] font-mono text-slate-400 dark:text-gray-500 truncate text-center">
        {url}
      </div>
    </div>
    {/* Browser Body */}
    <div className="relative aspect-video w-full overflow-hidden bg-white dark:bg-[#050505]">
      {children}
    </div>
  </div>
);

const HelpdeskMockup = () => (
  <div className="w-full h-full flex flex-col text-[8px] sm:text-[10px] select-none pointer-events-none">
    <div className="flex flex-1 overflow-hidden">
       {/* Sidebar */}
       <div className="w-12 bg-slate-900 dark:bg-black flex flex-col items-center py-4 gap-4 border-r border-slate-800 dark:border-white/5">
          <div className="w-6 h-6 rounded bg-blue-600"></div>
          <div className="w-4 h-4 rounded-full bg-white/10 mt-2"></div>
          <div className="w-4 h-4 rounded-full bg-white/10"></div>
          <div className="w-4 h-4 rounded-full bg-white/10"></div>
       </div>
       {/* Main Content */}
       <div className="flex-1 bg-slate-50 dark:bg-[#080808] flex flex-col">
          {/* Topbar */}
          <div className="h-10 bg-white dark:bg-[#111] border-b border-slate-200 dark:border-white/5 flex justify-between items-center px-4">
             <div className="w-24 h-2 bg-slate-200 dark:bg-white/10 rounded"></div>
             <div className="flex gap-2">
                <div className="w-6 h-6 rounded-full bg-slate-200 dark:bg-white/10"></div>
             </div>
          </div>
          {/* Dashboard */}
          <div className="p-4 space-y-4 overflow-hidden">
             <div className="grid grid-cols-3 gap-3">
                <div className="bg-white dark:bg-[#111] p-3 rounded border border-slate-200 dark:border-white/5 shadow-sm">
                   <div className="text-slate-400 dark:text-gray-600 mb-1">Pending</div>
                   <div className="text-xl font-bold text-blue-600 dark:text-blue-400">12</div>
                </div>
                <div className="bg-white dark:bg-[#111] p-3 rounded border border-slate-200 dark:border-white/5 shadow-sm">
                   <div className="text-slate-400 dark:text-gray-600 mb-1">Resolved</div>
                   <div className="text-xl font-bold text-emerald-600 dark:text-emerald-400">845</div>
                </div>
                <div className="bg-white dark:bg-[#111] p-3 rounded border border-slate-200 dark:border-white/5 shadow-sm">
                   <div className="text-slate-400 dark:text-gray-600 mb-1">Avg Time</div>
                   <div className="text-xl font-bold text-purple-600 dark:text-purple-400">2h</div>
                </div>
             </div>
             
             <div className="flex gap-4 h-32">
                <div className="flex-1 bg-white dark:bg-[#111] rounded border border-slate-200 dark:border-white/5 p-3">
                   <div className="w-20 h-2 bg-slate-200 dark:bg-white/10 rounded mb-4"></div>
                   <div className="flex items-end gap-2 h-16 px-2">
                      <div className="w-full bg-blue-500/20 h-[40%] rounded-t"></div>
                      <div className="w-full bg-blue-500/40 h-[70%] rounded-t"></div>
                      <div className="w-full bg-blue-500/60 h-[50%] rounded-t"></div>
                      <div className="w-full bg-blue-500/80 h-[90%] rounded-t"></div>
                      <div className="w-full bg-blue-500 h-[60%] rounded-t"></div>
                   </div>
                </div>
                <div className="w-1/3 bg-white dark:bg-[#111] rounded border border-slate-200 dark:border-white/5 p-3 space-y-2">
                   <div className="w-full h-8 bg-slate-50 dark:bg-white/5 rounded flex items-center px-2 gap-2">
                      <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                      <div className="w-10 h-1 bg-slate-200 dark:bg-white/10 rounded"></div>
                   </div>
                   <div className="w-full h-8 bg-slate-50 dark:bg-white/5 rounded flex items-center px-2 gap-2">
                      <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                      <div className="w-10 h-1 bg-slate-200 dark:bg-white/10 rounded"></div>
                   </div>
                   <div className="w-full h-8 bg-slate-50 dark:bg-white/5 rounded flex items-center px-2 gap-2">
                      <div className="w-2 h-2 rounded-full bg-red-500"></div>
                      <div className="w-10 h-1 bg-slate-200 dark:bg-white/10 rounded"></div>
                   </div>
                </div>
             </div>
          </div>
       </div>
    </div>
  </div>
);

const IoTMonitorMockup = () => (
  <div className="w-full h-full bg-[#050505] flex flex-col text-[8px] sm:text-[10px] font-mono p-4 relative overflow-hidden select-none pointer-events-none text-white">
      {/* Grid BG */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:10px_10px]"></div>
      
      <div className="flex justify-between items-center mb-4 z-10 border-b border-white/10 pb-2">
          <span className="text-emerald-500 font-bold">MACHINE_01 // ONLINE</span>
          <div className="flex gap-1 items-center">
              <span className="text-xs text-gray-500">PING: 24ms</span>
              <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse ml-2"></span>
          </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4 z-10">
           <div className="border border-white/10 bg-white/5 p-3 rounded relative overflow-hidden group">
               <div className="absolute top-0 right-0 p-1 opacity-20"><Activity size={24} /></div>
               <div className="text-gray-400 mb-1">VIBRATION</div>
               <div className="text-2xl font-bold text-cyan-400">2.4 <span className="text-xs text-gray-500 font-normal">mm/s</span></div>
               <div className="w-full bg-gray-800 h-1 mt-2 rounded-full overflow-hidden">
                  <div className="bg-cyan-500 h-full w-[45%]"></div>
               </div>
           </div>
           <div className="border border-white/10 bg-white/5 p-3 rounded relative overflow-hidden">
               <div className="absolute top-0 right-0 p-1 opacity-20"><Power size={24} /></div>
               <div className="text-gray-400 mb-1">TEMP</div>
               <div className="text-2xl font-bold text-amber-500">65 <span className="text-xs text-gray-500 font-normal">°C</span></div>
               <div className="w-full bg-gray-800 h-1 mt-2 rounded-full overflow-hidden">
                  <div className="bg-amber-500 h-full w-[65%]"></div>
               </div>
           </div>
      </div>

      <div className="flex-1 border border-white/10 bg-black/40 rounded p-2 z-10 flex flex-col justify-end relative">
           <div className="absolute top-2 left-2 text-xs text-gray-500">REAL-TIME SIGNAL</div>
           <div className="flex items-end gap-[2px] h-20 w-full overflow-hidden">
               {Array.from({length: 30}).map((_, i) => (
                   <div 
                    key={i} 
                    className="flex-1 bg-emerald-500/50 hover:bg-emerald-400 transition-all" 
                    style={{ height: `${20 + Math.random() * 60}%` }}
                   ></div>
               ))}
           </div>
      </div>
  </div>
);

export const ProjectsSection: React.FC = () => {
  const { t } = useLanguage();
  const projects = [
    { 
      id: "001", 
      title: "IT Helpdesk Pro Max", 
      desc: "Web-based support system with AI Chatbot integration for automated ticket triage and resolution suggestions.", 
      tech: ["PHP", "React", "MySQL", "OpenAI"], 
      status: "DEPLOYED", 
      link: "https://helpdesk.corestackdev.com",
      mockup: <HelpdeskMockup />,
      mockUrl: "helpdesk.corestackdev.com/dashboard"
    },
    { 
      id: "002", 
      title: "Vibration Monitor IoT", 
      desc: "Real-time dashboard monitoring industrial machine vibration sensors using ESP32 and WebSockets.", 
      tech: ["ESP32", "WebSocket", "Nginx", "InfluxDB"], 
      status: "ONLINE", 
      link: "https://realtime.corestackdev.com",
      mockup: <IoTMonitorMockup />,
      mockUrl: "iot.corestackdev.com/machine-01"
    },
    { 
      id: "003", 
      title: "Environment Reporter", 
      desc: "Automated legacy system modernization tool for generating custom PDF reports from raw sensor logs.", 
      tech: ["VB.NET", "Crystal Reports", "SQL"], 
      status: "STABLE", 
      link: "https://report.corestackdev.com",
      // No mockup for report gen (internal tool), simple fallback or different style
      mockup: null, 
      mockUrl: ""
    }
  ];

  return (
    <section id="projects" className="py-24 bg-white/80 dark:bg-black/80 border-y border-slate-200 dark:border-white/10 transition-colors duration-300 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-20">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white flex items-center gap-3 mb-4">
              <span className="text-emerald-600 dark:text-terminal-green">./</span>{t.projects.headline}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="text-slate-600 dark:text-gray-500 font-mono text-sm max-w-2xl border-l-2 border-emerald-600 dark:border-terminal-green pl-4">
              {t.projects.subhead}
            </p>
          </Reveal>
        </div>

        <div className="space-y-24">
          {projects.map((project, idx) => {
            const isEven = idx % 2 === 0;
            return (
              <Reveal key={project.id} width="100%">
                <div className={`flex flex-col ${isEven ? 'lg:flex-row' : 'lg:flex-row-reverse'} gap-12 lg:gap-20 items-center`}>
                   
                   {/* Project Details */}
                   <div className="flex-1 space-y-6">
                      <div className="flex items-center gap-4">
                         <span className="text-emerald-600 dark:text-terminal-green font-mono font-bold text-xl">0{idx + 1}</span>
                         <div className="h-px bg-slate-200 dark:bg-white/10 flex-1"></div>
                         <span className="px-3 py-1 bg-slate-100 dark:bg-white/5 text-[10px] font-mono uppercase tracking-widest text-slate-500 dark:text-gray-400 rounded border border-slate-200 dark:border-white/5">
                            {project.status}
                         </span>
                      </div>
                      
                      <h3 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white leading-tight">
                        {project.title}
                      </h3>
                      
                      <p className="text-slate-600 dark:text-gray-400 text-lg leading-relaxed">
                        {project.desc}
                      </p>

                      <div className="flex flex-wrap gap-2 pt-2">
                        {project.tech.map((t, i) => (
                          <span key={i} className="px-3 py-1.5 bg-white dark:bg-[#111] border border-slate-200 dark:border-white/10 text-xs text-slate-600 dark:text-gray-300 font-mono shadow-sm">
                            {t}
                          </span>
                        ))}
                      </div>

                      <div className="pt-4">
                        <a 
                          href={project.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-3 px-8 py-3 bg-slate-900 dark:bg-white text-white dark:text-black font-bold hover:bg-emerald-600 dark:hover:bg-terminal-green transition-all shadow-lg hover:shadow-emerald-500/20 group"
                        >
                          {t.projects.view_project} <ExternalLink size={16} className="group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform" />
                        </a>
                      </div>
                   </div>

                   {/* Project Preview (Mockup) */}
                   <div className="flex-1 w-full max-w-xl perspective-1000 group">
                      {project.mockup ? (
                        <motion.div 
                          whileHover={{ rotateY: isEven ? -5 : 5, scale: 1.02 }}
                          transition={{ type: "spring", stiffness: 100, damping: 20 }}
                          className="transform transition-all duration-500 shadow-2xl rounded-lg"
                        >
                           <BrowserFrame url={project.mockUrl}>
                              {project.mockup}
                           </BrowserFrame>
                           {/* Glow Effect */}
                           <div className="absolute inset-0 bg-emerald-500/10 dark:bg-terminal-green/5 blur-3xl -z-10 rounded-full opacity-50 group-hover:opacity-100 transition-opacity"></div>
                        </motion.div>
                      ) : (
                        // Fallback Card if no mockup
                        <div className="w-full aspect-video bg-slate-100 dark:bg-[#111] border border-slate-200 dark:border-white/10 rounded-lg flex items-center justify-center relative overflow-hidden group">
                           <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(68,107,158,0.05)_50%,transparent_75%,transparent_100%)] bg-[length:250%_250%] animate-[gradient_15s_ease_infinite]"></div>
                           <Terminal size={64} className="text-slate-300 dark:text-white/10" />
                           <div className="absolute bottom-4 right-4 text-xs font-mono text-slate-400">INTERNAL_TOOL</div>
                        </div>
                      )}
                   </div>

                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const AboutSection: React.FC = () => {
  const { t } = useLanguage();
  const jsonContent = `{
  "developer": {
    "name": "CoreStackDev",
    "mission": "GRIND_UNTIL_IT_WORKS",
    "traits": ["Resilience", "Obsession"],
    "status": "ALWAYS_LEARNING"
  }
}`;

  return (
    <section id="about" className="py-24 bg-slate-100/50 dark:bg-[#050505]/50 border-t border-slate-200 dark:border-white/10 transition-colors duration-300 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col lg:flex-row gap-16 items-center">
          
          <div className="w-full lg:w-1/2 space-y-8">
            <Reveal>
              <h2 className="text-3xl md:text-5xl font-bold text-slate-900 dark:text-white flex items-center gap-3">
                <span className="text-emerald-600 dark:text-terminal-green">./</span>{t.about.title}
              </h2>
            </Reveal>
            
            <div className="space-y-6 text-lg text-slate-600 dark:text-gray-400 leading-relaxed">
              <Reveal delay={0.1}>
                <p>
                  {t.about.text_1} <strong className="text-slate-900 dark:text-white">{t.about.text_1_bold}</strong>.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <p>
                  {t.about.text_2} <span className="text-cyan-600 dark:text-terminal-cyan">{t.about.text_2_accent}</span> {t.about.text_2_end}
                </p>
              </Reveal>
              
              <Reveal delay={0.3}>
                <div className="flex gap-4 pt-4">
                  <div className="bg-white dark:bg-terminal-dim/30 border border-slate-200 dark:border-white/10 p-6 rounded text-center min-w-[140px] shadow-sm">
                    <div className="text-4xl font-black text-slate-900 dark:text-white mb-2">99%</div>
                    <div className="text-xs text-emerald-600 dark:text-terminal-green uppercase tracking-wider font-bold">{t.about.stat_1}</div>
                  </div>
                  <div className="bg-white dark:bg-terminal-dim/30 border border-slate-200 dark:border-white/10 p-6 rounded text-center min-w-[140px] shadow-sm">
                    <div className="text-4xl font-black text-slate-900 dark:text-white mb-2">1%</div>
                    <div className="text-xs text-cyan-600 dark:text-terminal-cyan uppercase tracking-wider font-bold">{t.about.stat_2}</div>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>

          <div className="w-full lg:w-1/2">
            <Reveal delay={0.2}>
              <div className="bg-slate-900 dark:bg-[#0a0a0a] border border-slate-700 dark:border-terminal-dim rounded-lg overflow-hidden shadow-2xl relative group transform hover:rotate-1 transition-transform duration-500">
                <div className="bg-slate-800 dark:bg-[#1a1a1a] border-b border-slate-700 dark:border-terminal-dim p-3 flex items-center justify-between">
                  <div className="flex gap-2 ml-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-yellow-500/80"></div>
                    <div className="w-3 h-3 rounded-full bg-green-500/80"></div>
                  </div>
                  <div className="text-xs text-gray-400 font-mono opacity-50">profile.json</div>
                </div>
                
                <div className="p-8 font-mono text-sm md:text-base overflow-x-auto text-gray-300">
                  <div className="mb-4 text-gray-500">
                    user@corestackdev:~$ <span className="text-white">cat profile.json</span>
                  </div>
                  <pre className="text-emerald-400 dark:text-terminal-green">
                    <code dangerouslySetInnerHTML={{
                      __html: jsonContent
                        .replace(/"([^"]+)":/g, '<span class="text-cyan-400 dark:text-terminal-cyan">"$1"</span>:')
                        .replace(/: "([^"]+)"/g, ': <span class="text-white">"$1"</span>')
                    }} />
                  </pre>
                  <div className="mt-4">
                     user@corestackdev:~$ <span className="animate-cursor-blink bg-white w-2 h-4 inline-block align-middle ml-1"></span>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>

        </div>
      </div>
    </section>
  );
};

export const ContactSection: React.FC = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [logs, setLogs] = useState<string[]>([]);
  const [errorMsg, setErrorMsg] = useState<string>('');
  const bottomRef = useRef<HTMLDivElement>(null);

  // Auto-scroll logs
  useEffect(() => {
    if (status === 'sending' || status === 'success') {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, status]);

  const addLog = (msg: string) => {
     setLogs(prev => [...prev, msg]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMsg('');
    setLogs([]);

    // --- 1. Validation Logic ---
    if (!formData.name) {
        setStatus('error');
        setErrorMsg("[ ERROR ] : Field 'name' is required for handshake.");
        return;
    }
    if (!formData.email) {
        setStatus('error');
        setErrorMsg("[ ERROR ] : Field 'email' is required for handshake.");
        return;
    }
    if (!formData.message) {
        setStatus('error');
        setErrorMsg("[ ERROR ] : Field 'message' is empty. No payload to transmit.");
        return;
    }

    // --- 2. Sending Simulation ---
    setStatus('sending');
    
    // Sequence of "Terminal Logs"
    const sequence = [
        { msg: `[ SYSTEM ] : Initiating handshake with ${formData.email}...`, delay: 500 },
        { msg: `[ UPLOAD ] : Encrypting payload (${formData.message.length} bytes)...`, delay: 1000 },
        { msg: `[ NETWORK ] : Routing packet to 127.0.0.1 (CORE_MAIN)...`, delay: 1500 },
        { msg: `[ STATUS ] : Success (200 OK)`, delay: 2000 },
        { msg: `[ INFO ] : Packet successfully routed to corestackdev.com mainframe.`, delay: 2200 }
    ];

    for (const step of sequence) {
        await new Promise(r => setTimeout(r, step.delay - (sequence[sequence.indexOf(step)-1]?.delay || 0)));
        addLog(step.msg);
    }

    // --- 3. Success State ---
    setStatus('success');
  };

  const resetForm = () => {
      setFormData({ name: '', email: '', message: '' });
      setStatus('idle');
      setLogs([]);
      setErrorMsg('');
  };

  const InfoItem: React.FC<{ label: string; value: string; icon: React.ReactNode }> = ({ label, value, icon }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = () => {
      navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <motion.div 
        onClick={handleCopy}
        whileHover={{ x: 5 }}
        className="group cursor-pointer relative"
      >
        <div className="flex items-center gap-2 mb-1 text-[10px] md:text-xs font-mono text-slate-500 dark:text-gray-500">
           <span className="text-emerald-600 dark:text-terminal-green">root@corestack:~$</span>
           {label}
        </div>
        <div className="flex items-center gap-3 text-slate-700 dark:text-gray-300 group-hover:text-emerald-600 dark:group-hover:text-terminal-green transition-colors bg-black/5 dark:bg-black/30 p-3 rounded border border-transparent group-hover:border-emerald-600/30 dark:group-hover:border-terminal-green/30">
           {copied ? <Check size={18} /> : icon}
           <span className="font-mono text-sm break-all">{value}</span>
           <Copy size={14} className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </motion.div>
    );
  };

  const InputLabel: React.FC<{ label: string }> = ({ label }) => (
     <div className="flex items-center gap-1.5 mb-2 font-mono text-xs md:text-sm select-none">
        <span className="text-emerald-600 dark:text-terminal-green font-bold">user</span>
        <span className="text-slate-400">@</span>
        <span className="text-cyan-600 dark:text-terminal-cyan">corestackdev</span>
        <span className="text-slate-400">:~$</span>
        <span className="text-slate-800 dark:text-white ml-1">{label}</span>
     </div>
  );

  return (
    <section id="contact" className="py-32 relative overflow-hidden transition-colors duration-300">
      {/* Dimmed Background Overlay */}
      <div className="absolute inset-0 bg-slate-50/50 dark:bg-black/50 backdrop-blur-[2px] z-0 pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="text-center mb-16">
           <Reveal width="100%">
             <h2 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white mb-4 glitch-hover tracking-tight cursor-default">
              <span className="text-emerald-600 dark:text-terminal-green">MISSION</span>_CONTROL<span className="animate-pulse">_</span>
             </h2>
           </Reveal>
           <Reveal delay={0.1} width="100%">
             <div className="flex items-center justify-center gap-2 text-slate-600 dark:text-gray-500 font-mono text-sm">
                <Wifi size={14} className="animate-pulse text-emerald-600 dark:text-terminal-green" />
                {t.contact.subhead}
             </div>
           </Reveal>
        </div>

        <Reveal delay={0.3}>
          <div className="bg-white/60 dark:bg-slate-900/40 backdrop-blur-xl border border-blue-500/20 dark:border-emerald-500/30 rounded-xl shadow-[0_0_40px_rgba(59,130,246,0.1)] dark:shadow-[0_0_40px_rgba(57,255,20,0.1)] overflow-hidden">
             
             <div className="grid grid-cols-1 lg:grid-cols-2">
                
                {/* --- LEFT PANEL: SYSTEM INFO --- */}
                <div className="bg-slate-100/80 dark:bg-black/60 p-10 lg:p-14 border-b lg:border-b-0 lg:border-r border-slate-200 dark:border-white/10 relative overflow-hidden">
                   {/* Decorative Grid */}
                   <div className="absolute inset-0 opacity-5 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:20px_20px]"></div>

                   <div className="relative z-10 h-full flex flex-col justify-between gap-12">
                      <div>
                         <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-8 flex items-center gap-2">
                            <Terminal size={20} className="text-emerald-600 dark:text-terminal-green" />
                            SYSTEM_INFORMATION
                         </h3>
                         
                         <div className="space-y-6">
                            <InfoItem 
                               label="get_coordinates" 
                               value="[ 13.7563° N, 100.5018° E ]" 
                               icon={<MapPin size={18} className="text-emerald-600 dark:text-terminal-green" />} 
                            />
                            <InfoItem 
                               label="get_contact_email" 
                               value="contact@corestackdev.com" 
                               icon={<Mail size={18} className="text-emerald-600 dark:text-terminal-green" />} 
                            />
                            <InfoItem 
                               label="get_voice_channel" 
                               value="+66 XX-XXX-XXXX" 
                               icon={<Phone size={18} className="text-emerald-600 dark:text-terminal-green" />} 
                            />
                            <div className="grid grid-cols-2 gap-4 pt-2">
                              <InfoItem 
                                 label="git_user" 
                                 value="@corestackdev" 
                                 icon={<Github size={18} className="text-emerald-600 dark:text-terminal-green" />} 
                              />
                              <InfoItem 
                                 label="linked_in" 
                                 value="/in/corestack" 
                                 icon={<Linkedin size={18} className="text-emerald-600 dark:text-terminal-green" />} 
                              />
                            </div>
                         </div>
                      </div>

                      <div className="border-t border-slate-200 dark:border-white/10 pt-6">
                         <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                               <div className="relative flex h-3 w-3">
                                 <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                 <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                               </div>
                               <span className="font-mono text-sm text-slate-600 dark:text-gray-400 font-bold tracking-wider">SYSTEM ONLINE</span>
                            </div>
                            <span className="font-mono text-xs text-slate-400">V.2.4.0-STABLE</span>
                         </div>
                      </div>
                   </div>
                </div>

                {/* --- RIGHT PANEL: FORM --- */}
                <div className="bg-white/40 dark:bg-slate-900/40 p-10 lg:p-14 relative">
                   <AnimatePresence mode="wait">
                    {/* --- VIEW 1: THE FORM --- */}
                    {status !== 'success' && status !== 'sending' && (
                      <motion.form 
                        key="form"
                        initial={{ opacity: 0, x: 20 }} 
                        animate={{ opacity: 1, x: 0 }} 
                        exit={{ opacity: 0, x: -20 }}
                        transition={{ duration: 0.4 }}
                        onSubmit={handleSubmit} 
                        className="space-y-8 font-mono text-sm md:text-base relative z-10 h-full flex flex-col justify-center"
                      >
                        <div className="mb-2">
                           <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2">INITIALIZE_COMM</h3>
                           <p className="text-xs text-slate-500 dark:text-gray-500">Fill required fields to establish uplink.</p>
                        </div>
                        
                        {/* Error Banner */}
                        <AnimatePresence>
                          {status === 'error' && (
                            <motion.div 
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              className="bg-red-500/10 border border-red-500 text-red-600 dark:text-red-400 p-3 rounded flex items-center gap-3 overflow-hidden text-xs"
                            >
                               <AlertTriangle size={16} className="animate-pulse shrink-0" />
                               <span className="font-bold">{errorMsg}</span>
                            </motion.div>
                          )}
                        </AnimatePresence>

                        {/* Name Field */}
                        <div className="group relative">
                          <InputLabel label={t.contact.label_name} />
                          <div className="relative">
                            <input 
                              type="text" 
                              value={formData.name} 
                              onChange={(e) => setFormData({...formData, name: e.target.value})} 
                              className="w-full bg-slate-50/50 dark:bg-black/40 border border-slate-300 dark:border-slate-700 py-3 px-4 text-slate-900 dark:text-white focus:outline-none focus:border-blue-500 dark:focus:border-terminal-green transition-all duration-300 placeholder-transparent caret-blue-600 dark:caret-terminal-green rounded-sm"
                            />
                          </div>
                        </div>

                        {/* Email Field */}
                        <div className="group relative">
                          <InputLabel label={t.contact.label_email} />
                          <div className="relative">
                            <input 
                              type="email" 
                              value={formData.email} 
                              onChange={(e) => setFormData({...formData, email: e.target.value})} 
                              className="w-full bg-slate-50/50 dark:bg-black/40 border border-slate-300 dark:border-slate-700 py-3 px-4 text-slate-900 dark:text-white focus:outline-none focus:border-blue-500 dark:focus:border-terminal-green transition-all duration-300 placeholder-transparent caret-blue-600 dark:caret-terminal-green rounded-sm"
                            />
                          </div>
                        </div>

                        {/* Message Field */}
                        <div className="group relative">
                           <InputLabel label={t.contact.label_msg} />
                           <div className="relative">
                            <textarea 
                              rows={4} 
                              value={formData.message} 
                              onChange={(e) => setFormData({...formData, message: e.target.value})} 
                              className="w-full bg-slate-50/50 dark:bg-black/40 border border-slate-300 dark:border-slate-700 py-3 px-4 text-slate-900 dark:text-white focus:outline-none focus:border-blue-500 dark:focus:border-terminal-green transition-all duration-300 resize-none caret-blue-600 dark:caret-terminal-green rounded-sm"
                            />
                          </div>
                        </div>

                        {/* Submit Button */}
                        <div className="pt-2">
                          <button 
                            type="submit" 
                            className="w-full relative overflow-hidden group py-4 font-bold tracking-[0.2em] flex items-center justify-center gap-4 transition-all duration-300 rounded-sm border border-slate-300 dark:border-white/20 bg-slate-200 dark:bg-white/5 text-slate-700 dark:text-white hover:bg-white dark:hover:bg-white/10 hover:border-blue-500 dark:hover:border-terminal-green hover:shadow-[0_0_30px_rgba(59,130,246,0.3)] dark:hover:shadow-[0_0_30px_rgba(57,255,20,0.2)]"
                          >
                             <motion.div 
                               className="absolute inset-0 bg-gradient-to-r from-transparent via-blue-400/20 dark:via-terminal-green/20 to-transparent w-1/2 h-full skew-x-12"
                               animate={{ x: ['-150%', '350%'] }}
                               transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
                            />
                            <span className="text-blue-600 dark:text-terminal-green text-xl">{'>'}</span> 
                            <span className="relative z-10">SEND_PACKET</span>
                          </button>
                        </div>
                      </motion.form>
                    )}

                    {/* --- VIEW 2: LOGS & SUCCESS --- */}
                    {(status === 'sending' || status === 'success') && (
                       <motion.div
                         key="logs"
                         initial={{ opacity: 0 }}
                         animate={{ opacity: 1 }}
                         className="font-mono text-sm md:text-base space-y-4 h-full flex flex-col items-start justify-center w-full"
                       >
                          <div className="w-full bg-black/80 rounded-lg p-6 border border-slate-700 dark:border-terminal-dim h-[400px] overflow-y-auto custom-scrollbar relative shadow-inner flex flex-col">
                             {/* Header of Terminal */}
                             <div className="absolute top-0 left-0 right-0 bg-slate-800 p-2 flex items-center gap-2 px-4 border-b border-slate-700 shrink-0">
                                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                <span className="ml-2 text-xs text-gray-400">bash — transmission_log</span>
                             </div>
                             
                             {/* Logs Body */}
                             <div className="mt-8 space-y-2 flex-1">
                                 {logs.map((log, i) => (
                                   <motion.div 
                                     key={i}
                                     initial={{ opacity: 0, x: -10 }}
                                     animate={{ opacity: 1, x: 0 }}
                                     className="text-emerald-400 dark:text-terminal-green break-all"
                                   >
                                     {log}
                                   </motion.div>
                                 ))}
                                 {status === 'sending' && (
                                    <motion.div 
                                      animate={{ opacity: [0, 1, 0] }} 
                                      transition={{ repeat: Infinity, duration: 0.8 }}
                                      className="w-3 h-5 bg-emerald-500 dark:bg-terminal-green inline-block align-middle ml-1"
                                    />
                                 )}
                                 <div ref={bottomRef} />
                             </div>
                          </div>

                          {/* Success Banner Overlay */}
                          {status === 'success' && (
                            <motion.div 
                               initial={{ scale: 0.9, opacity: 0 }}
                               animate={{ scale: 1, opacity: 1 }}
                               transition={{ delay: 0.2, type: "spring" }}
                               className="w-full mt-2 bg-emerald-500/10 border border-emerald-500/50 p-4 rounded flex flex-col items-center justify-center gap-4 text-center"
                            >
                               <div className="flex items-center gap-2">
                                  <CheckCircle2 size={24} className="text-emerald-500 dark:text-terminal-green" />
                                  <h3 className="text-lg font-bold text-slate-900 dark:text-white">PACKET RECEIVED</h3>
                               </div>
                               
                               <button 
                                 onClick={resetForm}
                                 className="flex items-center gap-2 px-6 py-2 bg-emerald-600 dark:bg-terminal-green text-white dark:text-black font-bold rounded hover:bg-emerald-500 transition-colors shadow-lg hover:shadow-emerald-500/20 text-xs"
                               >
                                  <RefreshCw size={14} />
                                  SEND_ANOTHER
                               </button>
                            </motion.div>
                          )}
                       </motion.div>
                    )}
                   </AnimatePresence>
                </div>

             </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export const ConsultingSection: React.FC = () => {
  const { t } = useLanguage();
  const steps = [
    { icon: <Search size={32} />, title: t.consulting.step1_title, desc: t.consulting.step1_desc, color: "text-blue-600 dark:text-blue-400", border: "border-blue-600/30 dark:border-blue-400/30", bgBadge: "bg-blue-50 dark:bg-[#0f172a]" },
    { icon: <PenTool size={32} />, title: t.consulting.step2_title, desc: t.consulting.step2_desc, color: "text-cyan-600 dark:text-terminal-cyan", border: "border-cyan-600/30 dark:border-terminal-cyan/30", bgBadge: "bg-cyan-50 dark:bg-[#0f172a]" },
    { icon: <TrendingUp size={32} />, title: t.consulting.step3_title, desc: t.consulting.step3_desc, color: "text-emerald-600 dark:text-terminal-green", border: "border-emerald-600/30 dark:border-terminal-green/30", bgBadge: "bg-emerald-50 dark:bg-[#0f172a]" }
  ];

  return (
    <section className="py-24 bg-white/90 dark:bg-[#0f172a]/90 relative overflow-hidden border-y border-slate-200 dark:border-white/5 transition-colors duration-300 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <div className="mb-16 text-center">
             <Reveal width="100%">
               <h2 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-4 inline-block relative">
                  <span className="text-cyan-700 dark:text-terminal-cyan relative z-10">{t.consulting.headline}</span>
                  <span className="absolute -bottom-2 left-0 w-full h-1 bg-cyan-600/30 dark:bg-terminal-cyan/50"></span>
               </h2>
             </Reveal>
             <Reveal delay={0.2} width="100%">
                <p className="text-slate-600 dark:text-gray-400 font-mono text-sm mt-4">{t.consulting.subhead}</p>
             </Reveal>
        </div>

        <RevealGroup className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {steps.map((step, idx) => (
                <RevealItem key={idx}>
                  <div className={`bg-white/80 dark:bg-[#0f172a]/50 p-8 border-2 border-dashed ${step.border} relative group hover:bg-slate-50 dark:hover:bg-[#1e293b] transition-colors shadow-sm h-full`}>
                      <div className={`absolute -top-4 -left-4 w-8 h-8 ${step.color} ${step.bgBadge} border border-dashed ${step.border} flex items-center justify-center font-mono text-xs font-bold`}>{idx + 1}</div>
                      <div className={`mb-6 ${step.color} opacity-80 group-hover:opacity-100 transition-opacity`}>{step.icon}</div>
                      <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 font-mono">{step.title}</h3>
                      <p className="text-slate-600 dark:text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                  </div>
                </RevealItem>
            ))}
        </RevealGroup>
      </div>
    </section>
  );
};

export const TechStackSection: React.FC = () => {
  const { t } = useLanguage();
  const stack = [
    { category: "FRONTEND", items: ["React", "Vite", "Tailwind CSS", "Framer Motion"], icon: <Monitor size={20} />, color: "border-emerald-600 dark:border-terminal-green" },
    { category: "BACKEND", items: ["Node.js", "PostgreSQL", "PHP (Legacy)", "Python"], icon: <Database size={20} />, color: "border-cyan-600 dark:border-terminal-cyan" },
    { category: "HARDWARE / IoT", items: ["ESP32", "Arduino", "C++", "MQTT"], icon: <Cpu size={20} />, color: "border-purple-600 dark:border-purple-500" },
    { category: "TOOLS & DEVOPS", items: ["Docker", "Git", "Figma", "Linux"], icon: <Wrench size={20} />, color: "border-amber-500 dark:border-yellow-500" }
  ];

  return (
    <section className="py-20 bg-slate-50/80 dark:bg-black/80 border-b border-slate-200 dark:border-white/10 transition-colors duration-300 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center gap-4 mb-12 overflow-hidden">
            <Reveal>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white whitespace-nowrap">
                  <span className="text-emerald-700 dark:text-terminal-green">{t.techstack.headline}</span>
              </h2>
            </Reveal>
            <div className="h-[1px] bg-slate-300 dark:bg-white/20 w-full"></div>
        </div>

        <RevealGroup className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {stack.map((cat, idx) => (
                <RevealItem key={idx}>
                  <div className="bg-white dark:bg-[#0a0a0a] border border-slate-200 dark:border-white/10 p-5 group hover:border-slate-400 dark:hover:border-white/30 transition-colors shadow-sm h-full">
                      <div className={`flex items-center gap-3 mb-4 pb-3 border-b border-slate-100 dark:border-white/10 ${cat.color}`}>
                          <span className="text-slate-500 dark:text-gray-400 group-hover:text-slate-900 dark:group-hover:text-white transition-colors">{cat.icon}</span>
                          <h3 className="font-bold text-sm tracking-wider text-slate-900 dark:text-white">{cat.category}</h3>
                      </div>
                      <div className="space-y-2">
                          {cat.items.map((item, i) => (
                              <div key={i} className="flex items-center gap-2 text-sm text-slate-600 dark:text-gray-500 font-mono group-hover:text-emerald-700 dark:group-hover:text-terminal-green/80 transition-colors">
                                  <span className="w-1 h-1 bg-slate-400 dark:bg-gray-600 rounded-full"></span>
                                  {item}
                              </div>
                          ))}
                      </div>
                  </div>
                </RevealItem>
            ))}
        </RevealGroup>
      </div>
    </section>
  );
};

// --- MAIN LANDING PAGE COMPONENT ---

const LandingPage: React.FC<{ onNavigate: (view: string) => void }> = ({ onNavigate }) => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });

  return (
    <>
      {/* Scroll Progress Bar */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-emerald-500 dark:bg-terminal-green origin-left z-[100]"
        style={{ scaleX }}
      />

      <div className="flex flex-col">
        <HeroSection onNavigate={onNavigate} />
        <ServicesSection />
        <ConsultingSection />
        <ProjectsSection />
        <AboutSection />
        <TechStackSection />
        <ContactSection />
      </div>
    </>
  );
};

export default LandingPage;